import { motion } from "framer-motion";
import { ASSETS, FOOD, TABLE_MORE_IMAGES } from "../../data/content";
import { FadeUp, MaskLineInView } from "./Reveal";

export const TheFood = () => {
  return (
    <section
      id="food"
      data-testid="food-section"
      className="bg-[#fbf8f0] py-12 lg:py-16"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Text */}
          <div className="order-2 lg:order-1 lg:col-span-6">
            <FadeUp>
              <span className="font-body text-[#c56928] text-[13px] tracking-[0.3em] uppercase">
                {FOOD.label}
              </span>
            </FadeUp>
            <h2 className="font-display text-[#07412b] text-[52px] sm:text-7xl lg:text-[110px] leading-[1] mt-2 whitespace-pre-line">
              {FOOD.headline.split("\n").map((line, i) => (
                <MaskLineInView key={i} delay={i * 0.12}>
                  {line}
                </MaskLineInView>
              ))}
            </h2>
            <div className="gold-rule w-24 my-5" />
            <div className="space-y-4 max-w-lg">
              {FOOD.lines.map((l, i) => (
                <FadeUp key={i} delay={i * 0.06}>
                  <p
                    className={`font-body font-light leading-relaxed ${
                      i === 0
                        ? "text-[#07412b] text-xl sm:text-2xl"
                        : "text-[#07412b]/75 text-base sm:text-lg"
                    }`}
                  >
                    {l}
                  </p>
                </FadeUp>
              ))}
            </div>
          </div>

          {/* Framed image pair */}
          <div className="order-1 lg:order-2 lg:col-span-6">
            <div className="flex flex-col gap-5 sm:gap-6">
              {/* Main portrait — thali on Persian carpet */}
              <FadeUp>
                <div className="relative">
                  <div className="absolute -inset-3 sm:-inset-4 border border-[#ddb128] rounded-t-[999px] translate-x-3 translate-y-3" />
                  <motion.div
                    whileHover={{ scale: 1.015 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="relative overflow-hidden arch-top rounded-b-2xl shadow-[0_30px_60px_-25px_rgba(7,65,43,0.4)]"
                  >
                    <img
                      src={ASSETS.foodThali}
                      alt="A home-cooked thali served at The Brook"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-[380px] sm:h-[520px] lg:h-[560px] object-cover object-[center_35%]"
                    />
                  </motion.div>
                </div>
              </FadeUp>

              {/* Secondary — dosa breakfast, wide landscape frame */}
              <FadeUp delay={0.1}>
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="relative w-full overflow-hidden rounded-2xl shadow-[0_18px_40px_-24px_rgba(7,65,43,0.4)]"
                  data-testid="food-img-dosa"
                >
                  <img
                    src={ASSETS.foodDosa}
                    alt="Breakfast — dosa, aloo bhaji and egg"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-[240px] sm:h-[320px] lg:h-[340px] object-cover object-center"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#07412b]/70 to-transparent p-4">
                    <p className="font-script text-[#ddb128] text-xl sm:text-2xl">
                      Breakfast, on the lawn
                    </p>
                  </div>
                </motion.div>
              </FadeUp>
            </div>
          </div>
        </div>

        {/* More from the kitchen */}
        <div className="mt-10 lg:mt-14">
          <FadeUp>
            <p className="font-script text-[#ddb128] text-4xl sm:text-5xl leading-none">
              &amp; more from the kitchen
            </p>
          </FadeUp>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {TABLE_MORE_IMAGES.map((img, i) => (
              <motion.figure
                key={i}
                data-testid={`table-more-${i + 1}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 }}
                className="group relative overflow-hidden rounded-2xl shadow-[0_18px_40px_-24px_rgba(7,65,43,0.35)]"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[240px] sm:h-[300px] lg:h-[340px] object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#07412b]/75 via-[#07412b]/10 to-transparent p-4">
                  <p className="font-script text-[#ddb128] text-lg leading-tight">
                    {img.alt}
                  </p>
                </div>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
